const loadBoardView = {
  title: 'Load Board',
  toolbar: {
    addService: 'Add Service',
    addRoute: 'Add Route',
    search: {
      label: 'Search services',
      placeholder: 'Search services...',
    },
    loadSearch: {
      aria: 'Search loads...',
      placeholder: 'Search loads...',
    },
    selectDate: 'Select date',
    today: 'Today',
  },
  content: {
    noLoads: 'No loads available at the moment',
    availableLoads: 'Available Loads',
    error: 'Error loading loads: {{message}}',
  },
  columns: {
    completed: 'Completed',
    assigned: 'Assigned to Driver',
    pending: 'Pending Pickup',
    inTransit: 'In Transit',
    delayed: 'Delayed',
    documentation: 'Documentation Pending',
  },
  driverCard: {
    defaultTruck: 'Default Truck',
    defaultTrailer: 'Default Trailer',
    changeTruck: 'Change Truck',
    changeTrailer: 'Change Trailer',
    noTruck: 'No truck',
    noTrailer: 'No trailer',
    active: 'active',
    done: 'done',
    planned: 'planned',
    draft: 'draft',
    createTask: 'Create task',
    submitToQueue: 'Submit to queue',
    createRoute: 'Create route',
    printTasks: 'Print tasks',
  },
  tabs: {
    unassigned: 'Unassigned',
    requests: 'Requests',
  },
  pinnedEmpty: 'Supplier portal is not yet available',
  pendingToolbar: {
    message: "Pending tasks. Proceed adding them to drivers' queue.",
    submitAll: 'Submit all tasks',
    submitting: 'Submitting...',
  },
  errors: {
    revertToDraft: 'Failed to revert service to Draft.',
    transitionFailed:
      'Failed to transition service to Planned. Assignment was rolled back.',
    rollbackFailed:
      'Assignment succeeded but could not be rolled back — manual resolution needed.',
    assignFailed: 'Failed to assign service to driver.',
    columnOrderFailed: 'Failed to save column order.',
  },
  emptyState: {
    title: 'No loads found',
    description:
      "Try adjusting your search or filters to find what you're looking for.",
  },
};

const routesView = {
  toolbar: {
    search: 'Search routes...',
    delete: (count: number) =>
      count === 1 ? 'Delete' : `Delete ${count} routes`,
  },
  table: {
    aria: 'Routes List',
    columns: {
      id: 'ID',
      name: 'Name',
      routeDate: 'Route Date',
      driver: 'Driver',
      services: 'Services',
    },
    actions: {
      edit: 'Edit',
    },
  },
  content: {
    error: (message: string) => `Error loading routes: ${message}`,
    noData: 'No routes available',
  },
  status: {
    Planned: 'Planned',
    Active: 'Active',
    Completed: 'Completed',
    Failed: 'Failed',
    Voided: 'Voided',
  },
  messages: {
    deleteSuccess: (count: number) => `${count} route(s) deleted successfully`,
    error: 'An error occurred while processing your request',
  },
  form: {
    title: {
      create: 'New Route',
      edit: 'Edit Route',
      view: 'View Route',
    },
    fields: {
      facilityGroup: 'Facility Group',
      name: 'Route Name',
      driver: 'Assign Driver',
      routeDate: 'Route Date',
      status: 'Status',
    },
    placeholders: {
      name: 'Enter route name',
      driver: 'Select driver',
      status: 'Select status',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Route',
      save: 'Save Changes',
      activate: 'Activate Route',
      complete: 'Complete Route',
      void: 'Void Route',
      fail: 'Mark as Failed',
      edit: 'Edit Route',
    },
    messages: {
      createSuccess: 'Route created successfully',
      updateSuccess: 'Route updated successfully',
      activateSuccess: 'Route activated successfully',
      completeSuccess: 'Route completed successfully',
      voidSuccess: 'Route voided successfully',
      failSuccess: 'Route marked as failed',
      error: 'Failed to save route',
      facilityGroupRequired: 'Facility group is required',
      transitionSuccess: 'Route status updated successfully',
    },
    statuses: {
      Planned: 'Planned',
      Active: 'Active',
    },
    tabs: {
      services: 'Services',
      tasks: 'Tasks',
      routeMap: 'Route Map',
      routeMapWip: 'Route map view is coming soon.',
    },
    services: {
      availableServices: 'Available Services',
      routeServices: 'Route Services',
      noAvailableServices: 'No services available',
      noRouteServices: 'No services added to route',
      dragFromLeft: 'Drag services from the left panel',
      viewModeMessage: 'Services cannot be modified in view mode',
      noTasks: 'No tasks generated for this route',
      position: 'Position',
    },
  },
};

const servicesView = {
  form: {
    title: {
      create: 'New Service',
      edit: 'Edit Service',
      view: 'View Service',
      service: (num: number | string) =>
        `Service #${String(num).padStart(6, '0')}`,
    },
    modes: {
      simple: 'Simple',
      advanced: 'Advanced',
    },
    sections: {
      taskDetails: 'Task Details',
      location: 'Location',
      actionsAndBins: 'Actions & Bins',
      transportation: 'Transportation',
      otherDetails: 'Other Details',
    },
    fields: {
      type: 'Type',
      status: 'Status',
      rank: 'Rank',
      priority: 'Priority',
      location: 'Location',
      scheduledWindowStart: 'Start Window',
      scheduledWindowEnd: 'End Window',
      scheduledDate: 'Scheduled Date',
      description: 'Description',
      notes: 'Notes',
      internalNotes: 'Internal Notes',
      externalNotes: 'External Notes',
      route: 'Route',
      driver: 'Driver',
      truck: 'Truck',
      trailer: 'Trailer',
      facilityGroup: 'Facility Group',
      facility: 'Facility',
      destinationFacility: 'Destination Facility',
      estimatedArrival: 'Estimated Arrival',
      account: 'Account',
      hauler: 'Hauler',
      newLocation: 'New Location',
      existingLocation: 'Location',
      address: {
        saveLocation: 'Save Location',
        type: 'Address Type',
        location: 'Location',
        manualLocation: 'Manual Location',
        streetAddress: 'Street Address',
        city: 'City',
        state: 'State',
        postalCode: 'Postal Code',
        country: 'Country',
        account: 'Account',
        addressName: 'Address Name',
        locationName: 'Location Name',
        types: {
          standard: 'Standard',
          nonStandard: 'Non-Standard',
          poBox: 'PO Box',
        },
      },
      assets: {
        assets: 'Assets',
        assetGroup: (n: number) => `Asset Group ${n}`,
        add: 'Add Asset',
        none: 'No assets added',
        unknown: 'Unknown',
        selectSpecific: 'Select specific asset',
        asset: 'Asset',
        assetType: 'Asset Type',
        quantity: 'Quantity',
        locationRefs: {
          service_location: 'Service Location',
          facility: 'Facility',
          destination_facility: 'Destination Facility',
        } as Record<string, string>,
      },
    },
    placeholders: {
      facilityGroup: 'Select a facility group',
      facility: 'Select a facility',
      account: 'Select an account',
      location: 'Search by account / facility / peddler name or address',
      streetAddress: 'Enter street address',
      city: 'Enter city',
      state: 'Enter state',
      postalCode: 'Enter postal code',
      country: 'Enter country',
      selectAccount: 'Select an account',
      addressName: 'Enter location name',
      driver: 'Search for a driver...',
      truck: 'Search for a truck...',
      trailer: 'Search for a trailer...',
      description: 'Enter description',
      notes: 'Enter additional notes',
      hauler: 'Search for a hauler...',
      route: 'Search for a route...',
      asset: 'Search for an asset...',
    },
    buttons: {
      cancel: 'Cancel',
      remove: 'Remove',
      create: 'Create Service',
      saveAndAddLocation: 'Save & Add Location',
      complete: 'Complete Service',
      void: 'Void Service',
      save: 'Save Changes',
      edit: 'Edit Service',
      plan: 'Plan',
      start: 'Start',
      fail: 'Mark as Failed',
      confirm: 'Confirm',
    },
    messages: {
      createSuccess: 'Service created successfully',
      updateSuccess: 'Service updated successfully',
      voidSuccess: 'Service voided successfully',
      completeSuccess: 'Service completed successfully',
      error: 'Failed to save service',
      locationSaved: 'Location saved successfully',
      locationError: 'Failed to save location',
      planSuccess: 'Service planned successfully',
      startSuccess: 'Service started',
      failSuccess: 'Service marked as failed',
      transitionSuccess: 'Service status updated successfully',
    },
    reasonPrompts: {
      voidReason: 'Reason for voiding',
      failReason: 'Reason for failure',
      reasonPlaceholder: 'Enter reason...',
      confirm: 'Confirm',
    },
  },
  types: {
    pickup: 'Pickup',
    delivery: 'Delivery',
    maintenance: 'Maintenance',
    transfer: 'Transfer',
  },
  statuses: {
    draft: 'Draft',
    planned: 'Planned',
    inProgress: 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
    voided: 'Voided',
  },
  assignmentStatuses: {
    Unassigned: 'Unassigned',
    Offered: 'Offered',
    Accepted: 'Accepted',
    Rejected: 'Rejected',
    Released: 'Released',
  },
  driverProgress: {
    NotStarted: 'Not Started',
    EnRoute: 'En Route',
    OnSite: 'On Site',
    Completed: 'Completed',
    Failed: 'Failed',
  },
  haulerProgress: {
    NotStarted: 'Not Started',
    InProgress: 'In Progress',
    Completed: 'Completed',
    Failed: 'Failed',
  },
};

const assetsView = {
  title: 'Assets',
  toolbar: {
    addAsset: 'New Asset',
    search: {
      label: 'Search assets',
      placeholder: 'Search assets...',
    },
  },
  content: {
    noAssets: 'No assets available',
    availableAssets: 'Available Assets',
    error: (message: string) => `Error loading assets: ${message}`,
  },
  filters: {
    status: {
      all: 'All Statuses',
      inventory: 'Inventory',
      deployed: 'Deployed',
      inTransit: 'In Transit',
      outOfOrder: 'Out of Order',
    },
  },
  form: {
    title: {
      create: 'New Asset',
      edit: 'Edit Asset',
    },
    fields: {
      id: 'ID',
      identifier: 'Asset #',
      type: 'Asset Type',
      description: 'Description',
      status: 'Status',
      tareWeight: 'Tare Weight',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Asset',
      save: 'Save Changes',
    },
    messages: {
      createSuccess: 'Asset created successfully',
      updateSuccess: 'Asset updated successfully',
      error: 'Failed to save asset',
    },
  },
  detail: {
    tabs: {
      details: 'Details',
      location: 'Location',
    },
    sections: {
      info: 'Asset Information',
      associations: 'Associations',
      location: 'Location',
    },
    fields: {
      fillState: 'Fill State',
      notes: 'Notes',
      account: 'Account',
      facility: 'Facility',
      currentLocation: 'Current Location',
      lastLocationTime: 'Last Updated',
    },
    buttons: {
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      confirmDelete: 'Yes, delete this asset',
    },
    messages: {
      updateSuccess: 'Asset updated successfully',
      deleteSuccess: 'Asset deleted successfully',
      error: 'Failed to update asset',
    },
  },
};

const assetTypesView = {
  title: 'Asset Types',
  toolbar: {
    addType: 'New Asset Type',
    search: {
      label: 'Search asset types',
      placeholder: 'Search asset types...',
    },
  },
  content: {
    noTypes: 'No asset types defined',
    availableTypes: 'Available Asset Types',
    error: (message: string) => `Error loading asset types: ${message}`,
  },
  table: {
    columns: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      capacity: 'Capacity',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
    },
  },
  form: {
    title: {
      create: 'New Asset Type',
      edit: 'Edit Asset Type',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      capacity: 'Capacity',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Asset Type',
      save: 'Save Changes',
    },
    messages: {
      createSuccess: 'Asset type created successfully',
      updateNotImplemented: 'Update not implemented yet',
      error: 'Failed to save asset type',
    },
  },
};

const transportationTypes = {
  toolbar: {
    create: 'New Transportation Type',
    search: 'Search transportation types...',
    delete: (count: number) =>
      count === 1 ? 'Delete' : `Delete ${count} types`,
  },
  table: {
    aria: 'Transportation Types List',
    columns: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
    },
  },
  content: {
    error: (message: string) =>
      `Error loading transportation types: ${message}`,
  },
  dialog: {
    title: {
      create: 'New Transportation Type',
      edit: 'Edit Transportation Type',
    },
    fields: {
      name: 'Name',
      description: 'Description',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Transportation Type',
      save: 'Save Changes',
    },
  },
  messages: {
    createSuccess: 'Transportation type created successfully',
    updateSuccess: 'Transportation type updated successfully',
    deleteSuccess: (count: number) =>
      `${count} transportation type(s) deleted successfully`,
    error: 'Failed to save transportation type',
  },
};

const trucksView = {
  title: 'Trucks',
  toolbar: {
    create: 'New Truck',
    search: 'Search trucks...',
    delete: (count: number) =>
      count === 1 ? 'Delete' : `Delete ${count} trucks`,
  },
  table: {
    aria: 'Trucks List',
    columns: {
      id: 'ID',
      status: 'Status',
      licensePlate: 'License Plate',
      model: 'Model',
      transportationType: 'Transportation Type',
      capacity: 'Capacity',
      location: 'Location',
    },
  },
  content: {
    error: (message: string) => `Error loading trucks: ${message}`,
    noData: 'No trucks available',
  },
  status: {
    Available: 'Available',
    InUse: 'In Use',
    Inactive: 'Inactive',
    OutOfService: 'Out of Service',
  },
  form: {
    status: 'Status',
    transportationType: 'Transportation Type',
    licensePlate: 'License Plate',
    licensePlateExpiration: 'License Plate Expiration',
    model: 'Model',
    vin: 'VIN',
    capacity: 'Capacity',
    country: 'Country',
    state: 'State',
    notes: 'Notes',
    validation: {
      required: 'This field is required',
      invalidDate: 'Please enter a valid date',
      invalidNumber: 'Please enter a valid number',
    },
  },
  dialog: {
    title: {
      create: 'New Truck',
      edit: 'Edit Truck',
    },
    actions: {
      save: 'Save',
      create: 'Create',
      cancel: 'Cancel',
    },
  },
  messages: {
    createSuccess: 'Truck created successfully',
    updateSuccess: 'Truck updated successfully',
    deleteSuccess: (count: number) => `${count} truck(s) deleted successfully`,
    error: 'An error occurred while processing your request',
  },
  filters: {
    status: {
      all: 'All Statuses',
      available: 'Available',
      inUse: 'In Use',
      inactive: 'Inactive',
    },
  },
  emptyState: {
    title: 'No trucks found',
    description:
      "Try adjusting your search or filters to find what you're looking for.",
  },
};

const trailersView = {
  title: 'Trailers',
  toolbar: {
    create: 'New Trailer',
    search: 'Search trailers...',
    delete: (count: number) =>
      count === 1 ? 'Delete' : `Delete ${count} trailers`,
  },
  table: {
    aria: 'Trailers List',
    columns: {
      id: 'ID',
      status: 'Status',
      licensePlate: 'License Plate',
      transportationType: 'Transportation Type',
      capacity: 'Capacity',
      location: 'Location',
    },
  },
  content: {
    error: (message: string) => `Error loading trailers: ${message}`,
    noData: 'No trailers available',
  },
  status: {
    Available: 'Available',
    InUse: 'In Use',
    Inactive: 'Inactive',
    OutOfService: 'Out of Service',
  },
  form: {
    status: 'Status',
    transportationType: 'Transportation Type',
    licensePlate: 'License Plate',
    licensePlateExpiration: 'License Plate Expiration',
    vin: 'VIN',
    capacity: 'Capacity',
    country: 'Country',
    state: 'State',
    notes: 'Notes',
    validation: {
      required: 'This field is required',
      invalidDate: 'Please enter a valid date',
      invalidNumber: 'Please enter a valid number',
    },
  },
  dialog: {
    title: {
      create: 'New Trailer',
      edit: 'Edit Trailer',
    },
    actions: {
      save: 'Save',
      create: 'Create',
      cancel: 'Cancel',
    },
  },
  messages: {
    createSuccess: 'Trailer created successfully',
    updateSuccess: 'Trailer updated successfully',
    deleteSuccess: (count: number) =>
      `${count} trailer(s) deleted successfully`,
    error: 'An error occurred while processing your request',
  },
  filters: {
    status: {
      all: 'All Statuses',
      available: 'Available',
      inUse: 'In Use',
      inactive: 'Inactive',
    },
  },
  emptyState: {
    title: 'No trailers found',
    description:
      "Try adjusting your search or filters to find what you're looking for.",
  },
};

const driversView = {
  title: 'Drivers',
  toolbar: {
    create: 'New Driver',
    search: 'Search drivers...',
    delete: (count: number) =>
      count === 1 ? 'Delete' : `Delete ${count} drivers`,
  },
  table: {
    aria: 'Drivers List',
    columns: {
      id: 'ID',
      status: 'Status',
      address: 'Address',
      name: 'Name',
      licenseNumber: 'License Number',
      licenseExpiration: 'License Expiration',
      phone: 'Phone',
      email: 'Email',
      assignedTruck: 'Assigned Truck',
    },
  },
  content: {
    error: (message: string) => `Error loading drivers: ${message}`,
    noData: 'No drivers available',
  },
  status: {
    Available: 'Available',
    OnDuty: 'On Duty',
    OffDuty: 'Off Duty',
    OnBreak: 'On Break',
    Inactive: 'Inactive',
  },
  form: {
    status: 'Status',
    firstName: 'First Name',
    lastName: 'Last Name',
    licenseNumber: 'License Number',
    licenseExpiration: 'License Expiration',
    phone: 'Phone',
    email: 'Email',
    assignedTruck: 'Assigned Truck',
    notes: 'Notes',
    validation: {
      required: 'This field is required',
      invalidDate: 'Please enter a valid date',
      invalidEmail: 'Please enter a valid email address',
      invalidPhone: 'Please enter a valid phone number',
    },
  },
  dialog: {
    title: {
      create: 'New Driver',
      edit: 'Edit Driver',
    },
    actions: {
      save: 'Save',
      create: 'Create',
      cancel: 'Cancel',
    },
  },
  messages: {
    createSuccess: 'Driver created successfully',
    updateSuccess: 'Driver updated successfully',
    deleteSuccess: ({ count }: { count: number }) =>
      `${count} driver(s) deleted successfully`,
    error: 'An error occurred while processing your request',
  },
  filters: {
    status: {
      all: 'All Statuses',
      available: 'Available',
      onDuty: 'On Duty',
      offDuty: 'Off Duty',
      onBreak: 'On Break',
      inactive: 'Inactive',
    },
  },
  emptyState: {
    title: 'No drivers found',
    description:
      "Try adjusting your search or filters to find what you're looking for.",
  },
};

const serviceTypes = {
  predefinedNote:
    'Service types are predefined. You can customize the display name.',
  toolbar: {
    search: 'Search service types...',
  },
  table: {
    aria: 'Service Types List',
    columns: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      status: 'Status',
      createdAt: 'Created',
    },
    systemDefault: 'System',
    active: 'Active',
    inactive: 'Inactive',
  },
  content: {
    error: (message: string) => `Error loading service types: ${message}`,
  },
  emptyState: {
    title: 'No service types found',
    description: "Try adjusting your search to find what you're looking for.",
  },
  dialog: {
    title: {
      view: 'View Service Type',
      edit: 'Edit Service Type',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      isActive: 'Active',
    },
    taskDefinitions: {
      title: 'Task Definitions',
      empty: 'No task definitions configured',
      assetRole: 'Asset Role',
      taskType: 'Task Type',
      sequence: 'Sequence',
      locationRef: 'Location',
      locations: {
        A: 'Location A',
        B: 'Location B',
        C: 'Location C',
      },
      overridable: '(can override)',
      allowLocationOverride: 'Allow Location Override',
      taskTypes: {
        Pickup: 'Pickup',
        Delivery: 'Delivery',
        Fill: 'Fill',
        Empty: 'Empty',
        Reposition: 'Reposition',
      },
      assetRoles: {
        inbound: 'Inbound',
        outbound: 'Outbound',
        none: 'None',
      },
    },
    buttons: {
      cancel: 'Cancel',
      save: 'Save Changes',
      close: 'Close',
    },
  },
  messages: {
    updateSuccess: 'Service type updated successfully',
    error: 'Failed to save service type',
  },
};

const detentions = {
  toolbar: {
    search: 'Search detentions...',
    create: 'New Detention',
  },
  table: {
    aria: 'Detentions List',
    columns: {
      carrier: 'Carrier',
      location: 'Location',
      startDate: 'Start Date',
      endDate: 'End Date',
      freeMinutes: 'Free Minutes',
      ratePerMinute: 'Rate/Min',
      createdAt: 'Created',
    },
  },
  content: {
    error: (message: string) => `Error loading detentions: ${message}`,
  },
  emptyState: {
    title: 'No detentions found',
    description: "Try adjusting your search to find what you're looking for.",
  },
  dialog: {
    title: {
      create: 'New Detention',
      edit: 'Edit Detention',
      view: 'View Detention',
    },
    fields: {
      carrier: 'Carrier',
      location: 'Location',
      startDate: 'Start Date',
      endDate: 'End Date',
      freeMinutes: 'Free Minutes',
      ratePerMinute: 'Rate Per Minute',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Detention',
      save: 'Save Changes',
      close: 'Close',
      delete: 'Delete',
    },
  },
  messages: {
    createSuccess: 'Detention created successfully',
    updateSuccess: 'Detention updated successfully',
    deleteSuccess: 'Detention deleted successfully',
    error: 'Failed to save detention',
  },
};

const freightLanes = {
  toolbar: {
    search: 'Search freight lanes...',
    create: 'New Freight Lane',
  },
  table: {
    aria: 'Freight Lanes List',
    columns: {
      carrier: 'Carrier',
      transportationMode: 'Mode',
      equipmentType: 'Equipment',
      destination: 'Destination',
      status: 'Status',
      createdAt: 'Created',
    },
    active: 'Active',
    inactive: 'Inactive',
  },
  content: {
    error: (message: string) => `Error loading freight lanes: ${message}`,
  },
  emptyState: {
    title: 'No freight lanes found',
    description: "Try adjusting your search to find what you're looking for.",
  },
  dialog: {
    title: {
      create: 'New Freight Lane',
      edit: 'Edit Freight Lane',
      view: 'View Freight Lane',
    },
    fields: {
      carrier: 'Carrier',
      transportationMode: 'Transportation Mode',
      equipmentType: 'Equipment Type',
      commodityCategory: 'Commodity Category',
      destinationCity: 'City',
      destinationState: 'State',
      destinationZipCode: 'Zip Code',
      destinationCountry: 'Country',
      route: 'Route',
      active: 'Active',
      notes: 'Notes',
    },
    sections: {
      destination: 'Destination Area',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Freight Lane',
      save: 'Save Changes',
      close: 'Close',
      delete: 'Delete',
    },
  },
  messages: {
    createSuccess: 'Freight lane created successfully',
    updateSuccess: 'Freight lane updated successfully',
    deleteSuccess: 'Freight lane deleted successfully',
    error: 'Failed to save freight lane',
  },
  costs: {
    title: 'Cost History',
    add: 'Add Cost',
    empty: 'No cost records yet',
    fields: {
      startDate: 'Start Date',
      endDate: 'End Date',
      rateFormula: 'Rate Formula',
      chargebackFormula: 'Chargeback Formula',
      fuelSurchargeFormula: 'Fuel Surcharge Formula',
      fuelSurchargeMode: 'Fuel Surcharge Mode',
      minimumCharge: 'Minimum Charge',
      currency: 'Currency',
    },
    form: {
      titleCreate: 'New Cost Record',
      titleEdit: 'Edit Cost Record',
      cancel: 'Cancel',
      create: 'Add Cost',
      save: 'Save Cost',
    },
    messages: {
      createSuccess: 'Cost record created successfully',
      updateSuccess: 'Cost record updated successfully',
      deleteSuccess: 'Cost record deleted successfully',
      error: 'Failed to save cost record',
    },
  },
};

export const en = {
  loadBoardView,
  routesView,
  servicesView,
  assetsView,
  assetTypesView,
  transportationTypes,
  trucksView,
  trailersView,
  driversView,
  serviceTypes,
  detentions,
  freightLanes,
};
